import React, { useRef, useEffect, useState, useCallback } from "react";
import { api, apiForm } from "../../api/client";
import { useAuth } from "../../context/AuthContext";

function isImageFile(filename, contentType) {
  const ct = (contentType || "").toLowerCase();
  if (ct.startsWith("image/")) return true;
  const name = (filename || "").toLowerCase();
  return name.endsWith(".jpg") || name.endsWith(".jpeg") || name.endsWith(".png")
    || name.endsWith(".webp") || name.endsWith(".gif") || name.endsWith(".heic")
    || name.endsWith(".heif");
}

export default function QuestionMedia({ inspectionId, answerId, createdByUserId, onChanged }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef(null);
  
  const load = useCallback(async () => {
    if (!inspectionId || !answerId || !token) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await api(
        `/india/inspections/${inspectionId}/answers/${answerId}/media`,
        { token }
      );
      setItems(res.items || []);
    } catch (e) {
      setErr(e?.message || "Failed to load media");
    } finally {
      setLoading(false);
    }
  }, [inspectionId, answerId, token]);


  useEffect(() => {
    load(); // preload so we know if media exists (and can show the Show/Hide button after refresh)
  }, [load]);
  
  async function uploadFile(file) {
    if (!file) return;
    setUploading(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("answer_id", String(answerId));
      if (createdByUserId) fd.append("created_by_user_id", String(createdByUserId));

      await apiForm(`/india/inspections/${inspectionId}/media`, fd, { token });
      await load();
      onChanged?.();
      setOpen(true);
    } catch (e) {
      setErr(e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function openMedia(mediaId) {
    try {
      const res = await api(`/india/inspections/${inspectionId}/media/${mediaId}/download`, { token });
      if (res?.url) window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setErr(e?.message || "Failed to get download URL");
    }
  }

  async function deleteMedia(mediaId) {
    if (!window.confirm("Delete this file?")) return;
  
    setErr(null);
  
    try {
      await api(`/india/inspections/${inspectionId}/media/${mediaId}`, {
        token,
        method: "DELETE",
      });
  
      await load(); // reload media list
      onChanged?.();
    } catch (e) {
      setErr(e?.message || "Delete failed");
    }
  }
  

  return (
    <div style={{ marginTop: 8 }}>
      {items && items.length > 0 && (
        <button
          type="button"
          onClick={async () => {
            if (!open) await load();   // refresh before opening
            setOpen((prev) => !prev);
          }}          
          className="text-sm px-3 py-2 rounded text-white bg-petrol hover:opacity-90"
        >
          {open ? "Hide media" : "Show media"}
        </button>
      )}
  
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
        {/* Hidden native input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={async (e) => {
            const files = Array.from(e.target.files || []);
            for (const f of files) {
              await uploadFile(f);
            }
            e.target.value = "";
          }}
          disabled={uploading}
        />

        {/* Button logic */}
        {items?.length ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="text-xs px-2 py-1 rounded border bg-white hover:bg-gray-50 disabled:opacity-60"
            title="Upload more evidence"
          >
            Upload more evidence
          </button>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="text-sm px-3 py-2 rounded border bg-white hover:bg-gray-50 disabled:opacity-60"
            title="Upload evidence"
          >
            Upload evidence
          </button>
        )}

        {uploading ? <span className="text-sm opacity-70">Uploading…</span> : null}
        {loading ? <span className="text-sm opacity-70">Loading…</span> : null}
      </div>
  
      {err ? (
        <div style={{ color: "crimson", marginTop: 6 }}>
          {err}
        </div>
      ) : null}
  
      {/* Only show thumbnails when open AND there is media */}
      {open && items?.length ? (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
          {items.map((m) => (
            <div
              key={m.id}
              style={{
                width: 140,
                border: "1px solid #ddd",
                borderRadius: 8,
                padding: 8,
              }}
            >
              {isImageFile(m.file_name, m.content_type) ? (
                <MediaThumb
                  inspectionId={inspectionId}
                  mediaId={m.id}
                  onOpen={() => openMedia(m.id)}
                />
              ) : (
                <div style={{ fontSize: 12, marginBottom: 6 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {m.file_name}
                  </div>
                  <div style={{ opacity: 0.7 }}>
                    {Math.round((m.file_size || 0) / 1024)} KB
                  </div>
                </div>
              )}
  
              <button
                type="button"
                onClick={() => openMedia(m.id)}
                style={{ width: "100%", marginBottom: 6 }}
              >
                Open
              </button>
  
              <button
                type="button"
                onClick={() => deleteMedia(m.id)}
                style={{
                  width: "100%",
                  background: "#fff",
                  border: "1px solid #c00",
                  color: "#c00",
                }}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );  
}

function MediaThumb({ inspectionId, mediaId, onOpen }) {
  const [url, setUrl] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // For Day 06.3 simplicity: fetch a presigned URL per item for thumbnail display
      try {
        const res = await api(`/india/inspections/${inspectionId}/media/${mediaId}/download`, { token });
        if (!cancelled) setUrl(res?.url || null);
      } catch {
        if (!cancelled) setUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [inspectionId, mediaId, token]);

  if (!url) {
    return (
      <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.7 }}>
        Preview…
      </div>
    );
  }

  return (
    <img
      src={url}
      alt=""
      onClick={onOpen}
      style={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 6, cursor: "pointer", marginBottom: 6 }}
    />
  );
}
